import { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { getCurrentCompanyId } from '@/utils/auth';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getObjectInArrayByProperty = _wg('getObjectInArrayByProperty');
const loadNocSopImages = _wg('loadNocSopImages');

// ── Types ────────────────────────────────────────────────────────────

interface SopScreenProps {
  natureOfCallCodesWithSop: number[];
}

// ── Component ────────────────────────────────────────────────────────

export function SopScreen({ natureOfCallCodesWithSop }: SopScreenProps) {
  const { theme } = useTheme();
  const [sopData, setSopData] = useState('');

  useEffect(() => {
    if (!natureOfCallCodesWithSop || natureOfCallCodesWithSop.length === 0) {
      App.showPopupNotification('No SOP IDs found.', 'error');
      return;
    }

    const companyId = getCurrentCompanyId();

    const getNatureOfCallListPromise = (): Promise<any> => {
      return new Promise((resolve, reject) => {
        api.listNatureOfCall(
          (result: any) => resolve(result),
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting nature of call data.', 'error');
          }
        );
      });
    };

    const getStandardOperatingProceduresPromise = (data: any): Promise<any> => {
      return new Promise((resolve, reject) => {
        api.getStandardOperatingProcedures(
          data,
          (result: any) => resolve(result),
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting standard operating procedures data.', 'error');
          }
        );
      });
    };

    const data = {
      natureOfCallCodes: natureOfCallCodesWithSop,
      companyId: companyId,
    };

    Promise.all([getNatureOfCallListPromise(), getStandardOperatingProceduresPromise(data)])
      .then((values) => {
        const natureOfCallDataWithSop = (values[0] as any).natureOfCallList.filter(
          (e: any) => e.hasSop
        );
        const sopInfoList = values[1] as any[];

        // Create an array of promises to load images for each SOP
        const imageLoadPromises = sopInfoList.map((sopInfo) => {
          return new Promise<string>((resolve) => {
            const nocObj = getObjectInArrayByProperty(
              natureOfCallDataWithSop,
              'code',
              sopInfo.natureOfCallCode
            );

            loadNocSopImages(sopInfo.sop, sopInfo.natureOfCallCode, companyId, (processedSop: string) => {
              const htmlContent = `<div class="card card-primary"><div class="k-header k-grid-toolbar">${nocObj.value}</div><div class="card-body"><div class="card-block sop-card-block">${processedSop}</div></div></div>`;
              resolve(htmlContent);
            });
          });
        });

        // Wait for all images to load, then combine and display
        Promise.all(imageLoadPromises).then((htmlContentArray) => {
          setSopData(htmlContentArray.join(''));
        });
      });
  }, [natureOfCallCodesWithSop]);

  return (
    <div className={`container-fluid ${theme}`} style={{ padding: 0 }}>
      <div dangerouslySetInnerHTML={{ __html: sopData }} />
    </div>
  );
}

